import { formatDateTime } from "../../utils/helper";
import { Column, FleetrifyCustomTable } from "../CustomTable/customTable";
import React from "react";

export interface BillingHistory {
  id: string;
  date: string;
  invoice: string;
  plan: string;
  amount: string;
  status: string;
  transaction_reference?: string;
  payment_reference?: string;
}

const SubTable = ({
  onRowClick,
  tableData = [],
  pageSize,
  currentPage: currentPageProp,
  lastPage: lastPageProp,
  total,
  setCurrentPage: setCurrentPageProp,
}: {
  onRowClick: (param: BillingHistory) => void;
  tableData?: BillingHistory[];
  pageSize?: number;
  currentPage?: number;
  lastPage?: number;
  total?: number;
  setCurrentPage?: (page: number) => void;
}) => {
  const [expandedRowId, setExpandedRowId] = React.useState<string | null>(null);
  const [localPage, setLocalPage] = React.useState(1);

  if (!tableData) tableData = [];

  const effectivePageSize = pageSize ?? 10;
  const localTotalPage = Math.max(1, Math.ceil(tableData.length / effectivePageSize));
  const isControlledPagination =
    typeof currentPageProp === "number" &&
    typeof lastPageProp === "number" &&
    typeof setCurrentPageProp === "function";

  const currentPage = isControlledPagination ? currentPageProp : localPage;
  const setCurrentPage = isControlledPagination
    ? setCurrentPageProp!
    : setLocalPage;
  const lastPage = isControlledPagination ? lastPageProp : localTotalPage;
  const effectiveTotal = isControlledPagination ? total : tableData.length;

  React.useEffect(() => {
    if (!isControlledPagination && localPage > localTotalPage) {
      setLocalPage(localTotalPage);
    }
  }, [localPage, localTotalPage, isControlledPagination]);

  const paginatedData = isControlledPagination
    ? tableData
    : tableData.slice(
        (currentPage - 1) * effectivePageSize,
        currentPage * effectivePageSize
      );

  const handleRowClick = (row: BillingHistory) => {
    setExpandedRowId(prev => (prev === row.id ? null : row.id));
    if (onRowClick) onRowClick(row);
  };

  const columns: Column[] = [
    {
      label: "Date",
      key: "date",
      render: (value) => formatDateTime(value),
    },
    { label: "Invoice", key: "invoice" },
    { label: "Plan", key: "plan" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status" },
  ];

  return (
    <div>
      <FleetrifyCustomTable
        onRowClick={handleRowClick}
        data={paginatedData.map(row => ({
          ...row,
          _expandedContent:
            expandedRowId === row.id ? (
              <div className="bg-gray-50 p-4 border-t">
                <p><strong>Invoice:</strong> {row.invoice}</p>
                <p><strong>Plan:</strong> {row.plan}</p>
                <p><strong>Amount:</strong> {row.amount}</p>
                <p><strong>Status:</strong> {row.status}</p>
                <p><strong>Transaction Ref:</strong> {row.transaction_reference}</p>
                <p><strong>Payment Ref:</strong> {row.payment_reference}</p>
                <p><strong>Date:</strong> {formatDateTime(row.date)}</p>
              </div>
            ) : null,
        }))}
        columns={columns}
        tooltipLeft
        pageSize={effectivePageSize}
        currentPage={currentPage}
        lastPage={lastPage}
        total={effectiveTotal}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default SubTable;
